import {
  Chip,
  ChipRemove,
  ChipText,
  ChipsContainer,
  ChipsMedida,
  ChipContador,
  IconContainer,
  Checkbox,
  EmptyItem,
  ListItem,
  OptionText,
  OptionsList,
  OptionsText,
  SearchInput,
  SearchItem,
  SelectBody,
  SelectButton,
  SelectContainer,
  SelectTitle,
} from "./style";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FaCheck, FaChevronDown, FaTimes } from "react-icons/fa";

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export function Multiselect({
  options,
  title,
  onChange,
  value,
  limitPlaceholder = 0,
  searchable = false,
  searchPlaceholder = "Pesquisar",
  selectAll = false,
  selectAllLabel = "Selecionar todos",
  countPlaceholder = false,
  removable = false,
}: IMultiselectProps) {
  const [listVisible, setListVisible] = useState<boolean>(false);
  const [optionsCheckeds, setOptionsCheckeds] = useState<IMultiselectOptions[]>(options);
  const [busca, setBusca] = useState<string>("");
  const selectRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLSpanElement>(null);
  const chipsMedidaRef = useRef<HTMLSpanElement>(null);
  const [qtdChipsVisiveis, setQtdChipsVisiveis] = useState<number>(0);
  const [larguraChips, setLarguraChips] = useState<number>(0);

  const selecionados = useMemo(
    () => optionsCheckeds.filter((item) => item.checked),
    [optionsCheckeds]
  );

  const optionsVisiveis = useMemo(() => {
    const termo = busca.trim();
    if (!searchable || !termo) return options;

    const termoNormalizado = normalizar(termo);
    return options.filter((option) =>
      normalizar(option.label).includes(termoNormalizado)
    );
  }, [searchable, busca, options]);

  const todosVisiveisSelecionados = useMemo(
    () =>
      optionsVisiveis.length > 0 &&
      optionsVisiveis.every((option) =>
        optionsCheckeds.some((item) => item.value === option.value && item.checked)
      ),
    [optionsVisiveis, optionsCheckeds]
  );

  function handleCheckTodos() {
    const valoresVisiveis = optionsVisiveis.map((option) => option.value);
    const temporarioOptionsChecked = optionsCheckeds.map((item) =>
      valoresVisiveis.includes(item.value)
        ? { ...item, checked: !todosVisiveisSelecionados }
        : item
    );

    setOptionsCheckeds(temporarioOptionsChecked);

    const selectedValues = temporarioOptionsChecked
      .filter((option) => option.checked)
      .map((option) => option.value);
    onChange(selectedValues);
  }

  function handleCheckOption(checkedOption: any) {
    const temporarioOptionsChecked = [...optionsCheckeds];
    const optionIndex = temporarioOptionsChecked.findIndex(
      (item) => item.value === checkedOption
    );
    temporarioOptionsChecked[optionIndex].checked =
      !temporarioOptionsChecked[optionIndex].checked;

    setOptionsCheckeds(temporarioOptionsChecked);

    const selectedValues = temporarioOptionsChecked
      .map((option) => (option.checked ? option.value : null))
      .filter((item) => item !== null);
    onChange(selectedValues);
  }

  function handlePlaceholder() {
    const labelsArray = optionsCheckeds
      .map((item) => {
        if (item.checked) {
          return item.label;
        }

        return null;
      })
      .filter((item) => item !== null);

    if (countPlaceholder && labelsArray.length > 1) {
      return `${labelsArray.length} selecionados`;
    }

    let placeholder = `${labelsArray.join(", ")}`;
    if (limitPlaceholder && placeholder.length > limitPlaceholder) {
      placeholder = `${placeholder.slice(0, limitPlaceholder)}...`;
    }

    return placeholder;
  }

  useEffect(() => {
    const container = chipsRef.current;
    if (!removable || !container) return;

    const observer = new ResizeObserver(() =>
      setLarguraChips(container.clientWidth)
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [removable, selecionados.length > 0]);

  useLayoutEffect(() => {
    const medida = chipsMedidaRef.current;
    if (!removable || !medida || !larguraChips) return;

    const gap = 4;
    const larguraContador = 40;
    const larguras = Array.from(medida.children).map(
      (chip) => (chip as HTMLElement).offsetWidth
    );
    const total = larguras.reduce((soma, largura, i) => soma + largura + (i ? gap : 0), 0);

    if (total <= larguraChips) {
      setQtdChipsVisiveis(larguras.length);
      return;
    }

    let usado = larguraContador + larguras[0];
    let qtd = 1;
    while (qtd < larguras.length && usado + gap + larguras[qtd] <= larguraChips) {
      usado += gap + larguras[qtd];
      qtd++;
    }

    const larguraMinimaChip = 60;
    if (qtd < larguras.length && larguraChips - usado - gap >= larguraMinimaChip) {
      qtd++;
    }

    setQtdChipsVisiveis(qtd);
  }, [removable, selecionados, larguraChips]);

  function renderChip(item: IMultiselectOptions, encolher = false) {
    return (
      <Chip key={item.value} title={item.label} $encolher={encolher}>
        <ChipText>{item.label}</ChipText>
        <ChipRemove
          type="button"
          aria-label={`Remover ${item.label}`}
          onClick={(e) => {
            e.stopPropagation();
            handleCheckOption(item.value);
          }}
        >
          <FaTimes />
        </ChipRemove>
      </Chip>
    );
  }

  function renderChips() {
    const qtd = larguraChips ? qtdChipsVisiveis : selecionados.length;
    const visiveis = selecionados.slice(0, qtd);
    const ocultos = selecionados.slice(qtd);

    return (
      <ChipsContainer ref={chipsRef}>
        <ChipsMedida ref={chipsMedidaRef} aria-hidden>
          {selecionados.map((item) => renderChip(item))}
        </ChipsMedida>
        {visiveis.map((item, i) =>
          renderChip(item, i === visiveis.length - 1)
        )}
        {ocultos.length > 0 && (
          <ChipContador title={ocultos.map((item) => item.label).join(", ")}>
            +{ocultos.length}
          </ChipContador>
        )}
      </ChipsContainer>
    );
  }

  function handleClickOutside(event: MouseEvent) {
    if (
      selectRef.current &&
      !selectRef.current.contains(event.target as Node)
    ) {
      setListVisible(false);
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setOptionsCheckeds(
      options.map((option) => ({
        ...option,
        checked: value.includes(option.value as never),
      }))
    );
  }, [value]);

  return (
    <SelectContainer>
      {title && <SelectTitle>{title}</SelectTitle>}
      <SelectBody ref={selectRef}>
        <SelectButton
          onClick={() =>
            setListVisible((prev) => {
              if (prev) setBusca("");
              return !prev;
            })
          }
        >
          {optionsCheckeds.some((item) => item.checked) ? (
            removable ? (
              renderChips()
            ) : (
              <OptionText>{handlePlaceholder()}</OptionText>
            )
          ) : (
            "Selecione"
          )}
          <IconContainer>
            <FaChevronDown />
          </IconContainer>
        </SelectButton>
        <OptionsList isVisible={listVisible} >
          {searchable && (
            <SearchItem>
              <SearchInput
                type="text"
                value={busca}
                placeholder={searchPlaceholder}
                onChange={(e) => setBusca(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </SearchItem>
          )}
          {searchable && !optionsVisiveis.length && (
            <EmptyItem>Nenhum resultado encontrado</EmptyItem>
          )}
          {selectAll && !!optionsVisiveis.length && (
            <ListItem onClick={handleCheckTodos}>
              <Checkbox>
                {todosVisiveisSelecionados ? <FaCheck /> : <div></div>}
              </Checkbox>
              <OptionsText>{selectAllLabel}</OptionsText>
            </ListItem>
          )}
          {optionsVisiveis.map((option) => (
            <ListItem
              key={option.value}
              onClick={() => handleCheckOption(option.value)}
            >
              <Checkbox key={option.value}>
                {optionsCheckeds.some(
                  (item) => item.value === option.value && item.checked
                ) ? (
                  <FaCheck />
                ) : (
                  <div></div>
                )}
              </Checkbox>
              <OptionsText>{option.label}</OptionsText>
            </ListItem>
          ))}
        </OptionsList>
      </SelectBody>
    </SelectContainer>
  );
}
